import { useMemo } from "react";
import LoyaltyOffer from "../../../utils/LoyaltyOfferClass/LoyaltyOffer";
import { LoyaltyItemOffer } from "../../../utils/LoyaltyOfferClass/LoyaltyItemOffer";
import { offerSelectionTotals } from "../../../utils/loyalty/offerItemPricing";
import {
  buildSelectionGroups,
  flattenSelection,
  forcedSelectionState,
  isSupportedGuestBuiltOffer,
} from "../../../utils/loyalty/offerSelection";
import { useMenuLookups } from "../useMenuLookups";

type FixedOfferPricing = {
  /** What the guest pays for the offer. */
  price: number;
  /** What the same items cost without it. */
  priceBefore: number;
};

/**
 * The price of a BOGO or bundle the guest has nothing to choose in, quoted up
 * front: before and after the offer, priced exactly as the offer page prices
 * its selection (`offerSelectionTotals`), so the two never disagree.
 *
 * Undefined while there is a choice — no total exists until it is made — and
 * when any target item is missing from the menu: the offer page leaves those
 * out, and a forced set short of one would understate both figures.
 */
export const useFixedOfferPricing = (
  offer: LoyaltyOffer | undefined
): FixedOfferPricing | undefined => {
  const { subItemLookup } = useMenuLookups();

  return useMemo(() => {
    if (
      !(offer instanceof LoyaltyItemOffer) ||
      !isSupportedGuestBuiltOffer(offer)
    ) {
      return undefined;
    }

    const groups = buildSelectionGroups(
      offer,
      (entityId) => subItemLookup[entityId]
    );
    const targetCount = [
      offer.getBuyTargetConfig(),
      offer.getRewardTargetConfig(),
    ].reduce((sum, config) => sum + (config?.targetEntities?.length ?? 0), 0);
    const resolvedCount = groups.reduce(
      (sum, group) => sum + group.entities.length,
      0
    );
    if (resolvedCount !== targetCount) {
      return undefined;
    }

    const state = forcedSelectionState(groups);
    if (!state) {
      return undefined;
    }

    const { total, totalBeforeOffer } = offerSelectionTotals(
      offer,
      flattenSelection(groups, state),
      (entry) => Number(entry.entity.subItem.price ?? 0)
    );

    return { price: total, priceBefore: totalBeforeOffer };
  }, [offer, subItemLookup]);
};
